import React, { useState } from 'react';
import { Button, Form, Spinner, Badge } from 'react-bootstrap';
import { FaCheck, FaTimes, FaTrash, FaExclamationTriangle, FaEdit, FaSave, FaCheckSquare } from 'react-icons/fa';

const BAR_STYLE = {
  position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1050,
  color: 'white',
  padding: '10px 16px',
  paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
  boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
  transition: 'background 0.2s',
};

export default function BulkEditBar({
  // mode
  isEditing,
  selectMode,
  dirtyCount,
  // normal + edit mode actions
  onEditList,
  onSave,
  onCancelEdit,
  onToggleSelect,
  // select mode actions
  selectedCount,
  selectedIds,
  onApply,
  onDelete,
  onCancelSelect,
  // data
  roomList,
  containerList,
  categories,
  t,
}) {
  const [fields, setFields] = useState({ room: '', container: '', condition: '', type: '', year_purchased: '' });
  const [applying, setApplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hasChanges = Object.values(fields).some(v => v !== '');

  const handleApply = async () => {
    const nonEmpty = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== ''));
    setApplying(true);
    try {
      await onApply(nonEmpty);
      setFields({ room: '', container: '', condition: '', type: '', year_purchased: '' });
    } finally {
      setApplying(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    try {
      await onDelete();
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Select mode: delete confirmation ──
  if (selectMode && confirmDelete) {
    return (
      <div style={{ ...BAR_STYLE, background: '#842029' }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <FaExclamationTriangle className="flex-shrink-0" />
          <span className="flex-grow-1" style={{ fontSize: '0.9rem' }}>
            {t.bulkDeleteWarning.replace('{n}', selectedCount)}
          </span>
          <Button
            variant="danger" size="sm"
            onClick={handleDeleteConfirmed}
            disabled={deleting}
            style={{ background: '#dc3545', borderColor: '#dc3545', whiteSpace: 'nowrap' }}
          >
            {deleting
              ? <Spinner size="sm" animation="border" className="me-1" />
              : <FaTrash className="me-1" />}
            {t.bulkDeleteConfirm.replace('{n}', selectedCount)}
          </Button>
          <Button variant="outline-light" size="sm" onClick={() => setConfirmDelete(false)} disabled={deleting}>
            <FaTimes className="me-1" />{t.cancel}
          </Button>
        </div>
      </div>
    );
  }

  // ── Select mode ──
  if (selectMode) {
    return (
      <div style={{ ...BAR_STYLE, background: 'var(--bs-primary)' }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg="light" text="dark" className="px-3 py-2 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
            {selectedCount} {t.selected}
          </Badge>

          <Form.Select size="sm" style={{ width: 'auto', minWidth: '120px' }} value={fields.room}
            onChange={e => setFields(p => ({ ...p, room: e.target.value }))}>
            <option value="">{t.room}…</option>
            {roomList.map(r => <option key={r} value={r}>{r}</option>)}
          </Form.Select>

          <Form.Select size="sm" style={{ width: 'auto', minWidth: '130px' }} value={fields.container}
            onChange={e => setFields(p => ({ ...p, container: e.target.value }))}>
            <option value="">{t.storage}…</option>
            {containerList.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>

          <Form.Select size="sm" style={{ width: 'auto', minWidth: '120px' }} value={fields.condition}
            onChange={e => setFields(p => ({ ...p, condition: e.target.value }))}>
            <option value="">{t.cond}…</option>
            <option>Excellent</option>
            <option>Good</option>
            <option>Fair</option>
            <option>Poor</option>
          </Form.Select>

          <Form.Select size="sm" style={{ width: 'auto', minWidth: '160px' }} value={fields.type}
            onChange={e => setFields(p => ({ ...p, type: e.target.value }))}>
            <option value="">{t.type}…</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>

          <Form.Control
            type="number"
            size="sm"
            placeholder={t.yearPurchased}
            value={fields.year_purchased}
            onChange={e => setFields(p => ({ ...p, year_purchased: e.target.value }))}
            style={{ width: '110px' }}
            min="1900"
            max="2099"
          />

          <Button
            variant="danger" size="sm"
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => setConfirmDelete(true)}
          >
            <FaTrash className="me-1" />{t.bulkDelete}
          </Button>

          <div className="ms-auto d-flex gap-2">
            <Button variant="light" size="sm" disabled={!hasChanges || applying} onClick={handleApply}>
              {applying
                ? <Spinner size="sm" animation="border" className="me-1" />
                : <FaCheck className="me-1" />}
              {t.bulkApply}
            </Button>
            <Button variant="outline-light" size="sm" onClick={onCancelSelect}>
              <FaTimes className="me-1" />{t.cancel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Edit mode ──
  if (isEditing) {
    return (
      <div style={{ ...BAR_STYLE, background: '#198754' }}>
        <div className="d-flex align-items-center gap-2">
          {dirtyCount > 0 && (
            <Badge bg="light" text="dark" className="px-3 py-2" style={{ fontSize: '0.85rem' }}>
              {dirtyCount} {t.item}{dirtyCount !== 1 ? 's' : ''}
            </Badge>
          )}
          <div className="ms-auto d-flex gap-2">
            <Button variant="light" size="sm" className="text-success fw-semibold" onClick={onSave}>
              <FaSave className="me-1" />{t.save}
            </Button>
            <Button variant="outline-light" size="sm" onClick={onCancelEdit}>
              <FaTimes className="me-1" />{t.cancel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal mode ──
  return (
    <div style={{ ...BAR_STYLE, background: '#212529' }}>
      <div className="d-flex align-items-center gap-2 justify-content-end">
        <Button variant="outline-light" size="sm" onClick={onToggleSelect}>
          <FaCheckSquare className="me-1" />{t.selectItems}
        </Button>
        <Button variant="light" size="sm" className="text-dark fw-semibold" onClick={onEditList}>
          <FaEdit className="me-1" />{t.editList}
        </Button>
      </div>
    </div>
  );
}
